<template>
  <v-row class="clock-widget">
    <div v-if="hours" class="widget-content">
      <v-layout row class="newClock" @click.stop="openDialog">
        <v-flex xs12>
          <div class="content">
            <div class="content-left">
              <span v-if="useData.hourFormat === 'h24'">
                <span v-if="hours > 9">{{ hours }}:{{ minutes }}</span>
                <span v-else>0{{ hours }}:{{ minutes }}</span>
              </span>
              <span v-else>
                <span>{{ hours12 }}:{{ minutes }}</span>
              </span>
            </div>
            <div class="content-right">
              <div class="right-time">
                <div v-if="useData.showSeconds" class="right-top">
                  {{ seconds }}
                </div>
                <div v-else class="right-top noSeconds"></div>
                <div v-if="useData.hourFormat === 'h12'" class="right-bottom">
                  {{ hourtime }}
                </div>
                <div v-else class="right-bottom noAmPm"></div>
              </div>
            </div>
          </div>
        </v-flex>
        <v-flex xs12 class="date-container">
          <div v-if="useData.showDayName" class="dateDay">
            <span class="day">{{ getDayName }}</span>
          </div>
          <div v-if="useData.showDate" class="dateDay">
            <span v-if="useData.dateFormat === 'MMDDYYYY'" class="month">
              {{ getMonthName }} {{ thedate }}, {{ year }}
            </span>
            <span v-if="useData.dateFormat === 'DDMMYYYY'" class="month">
              {{ thedate }} {{ getMonthName }}, {{ year }}
            </span>
            <span v-if="useData.dateFormat === 'mmddyyyy'" class="month">
              {{ month }} / {{ thedate }} / {{ year }}
            </span>
            <span v-if="useData.dateFormat === 'ddmmyyyy'" class="month">
              {{ thedate }} / {{ month }} / {{ year }}
            </span>
          </div>
        </v-flex>
      </v-layout>
      <div class="remove-btn clock">
        <remove-button :widget-position="currentWidget" :widget-name="widgetName" />
      </div>
    </div>
    <v-dialog v-model="dialog" scrollable max-width="500" dark>
      <widgetForm
        :show-form="showForm"
        :widget-schema="formSchema"
        :widget-values="formValues"
        :widget-name="widgetName"
        :widget-position="currentWidget"
        @closeDialog="closeDialog"
      />
    </v-dialog>
  </v-row>
</template>

<script>
import WidgetForm from '../../components/Forms/widgetForm'
import RemoveButton from '../../components/Buttons/RemoveButton'
import { getHourTime, getZeroPad } from './filters'

export default {
  name: 'Clock',
  components: {
    WidgetForm,
    RemoveButton
  },
  props: {
    widgetData: {
      type: Object,
      required: false
    },
    widgetName: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      dialog: false,
      showForm: false,
      currentWidget: this.$attrs.position,
      formValues: {},
      formSchema: {},
      year: 0,
      hours: 0,
      hours12: 0,
      minutes: 0,
      seconds: 0,
      month: 0,
      dayNumber: 0,
      hourtime: null,
      dayName: [
        'sunday',
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday'
      ],
      monthName: [
        'january',
        'february',
        'march',
        'april',
        'may',
        'june',
        'july',
        'august',
        'september',
        'october',
        'november',
        'december'
      ]
    }
  },
  computed: {
    /* Create defaults for this widget */
    useData() {
      return (
        this.widgetData || {
          hourFormat: 'h12',
          showSeconds: true,
          showDate: true,
          showDayName: true,
          dateFormat: 'DDMMYY'
        }
      )
    },
    getDayName() {
      const dayNumber = new Date().getDay()
      return this.$i18n.t(`time.days.${this.dayName[dayNumber]}`)
    },
    getMonthName() {
      const monthNumber = new Date().getMonth()
      return this.$i18n.t(`time.months.${this.monthName[monthNumber]}`)
    }
  },
  mounted() {
    this.$options.interval = setInterval(this.updateDateTime, 1000)
  },
  beforeDestroy() {
    clearInterval(this.$options.interval)
  },
  methods: {
    openDialog() {
      this.setFormSchema()
      this.setFormValues()
      this.dialog = true
      this.showForm = true
    },
    setFormSchema() {
      const monthNumber = new Date().getMonth()
      const dateNumber = new Date().getDate()
      const yearNumber = new Date().getFullYear()
      const showMonthName = this.$i18n.t(
        `time.months.${this.monthName[monthNumber]}`
      )
      const formSchema = {
        hourFormat: {
          type: 'radio',
          label: this.$i18n.t('widgets.clock.form.hourFormat'),
          options: [
            {
              label: this.$i18n.t('time.freq.12h'),
              value: 'h12'
            },
            {
              label: this.$i18n.t('time.freq.24h'),
              value: 'h24'
            }
          ],
          flex: 6
        },
        showSeconds: {
          type: 'switch',
          label: this.$i18n.t('widgets.clock.form.showSeconds'),
          flex: 6
        },
        showDayName: {
          type: 'switch',
          label: this.$i18n.t('widgets.clock.form.showDayName'),
          flex: 6
        },
        showDate: {
          type: 'switch',
          label: this.$i18n.t('widgets.clock.form.showDate'),
          flex: 6
        },
        dateFormat: {
          type: 'select',
          label: this.$i18n.t('widgets.clock.form.dateFormat'),
          items: [
            {
              text: `${showMonthName} ${dateNumber}, ${yearNumber}`,
              value: 'MMDDYYYY'
            },
            {
              text: `${dateNumber} ${showMonthName}, ${yearNumber}`,
              value: 'DDMMYYYY'
            },
            {
              text: `${monthNumber} / ${dateNumber} / ${yearNumber}`,
              value: 'mmddyyyy'
            },
            {
              text: `${dateNumber} / ${monthNumber} / ${yearNumber}`,
              value: 'ddmmyyyy'
            }
          ],
          flex: 12
        }
      }
      this.formSchema = formSchema
    },
    setFormValues() {
      this.formValues.hourFormat = this.useData.hourFormat || null
      this.formValues.showSeconds = this.useData.showSeconds || null
      this.formValues.showDate = this.useData.showDate || null
      this.formValues.dateFormat = this.useData.dateFormat || null
      this.formValues.showDayName = this.useData.showDayName || null
    },
    changeWidget(widget) {
      this.dialog = false
      this.$store.dispatch('dashboard/updateWidgetType', {
        [this.currentWidget]: widget
      })
    },
    removeWidget() {
      this.dialog = false
      this.$store.dispatch('dashboard/updateWidgetType', {
        [this.currentWidget]: 'blank'
      })
    },
    /* Close the dialog and reset the props so that state aligns */
    closeDialog() {
      this.dialog = false
      this.showForm = false
      this.setFormValues()
      this.formSchema = {}
    },
    updateDateTime() {
      const now = new Date()
      this.year = now.getFullYear()
      this.thedate = now.getDate()
      this.month = now.getMonth()
      this.hours = now.getHours()
      this.hours12 = now.getHours()
      this.minutes = getZeroPad(now.getMinutes())
      this.seconds = getZeroPad(now.getSeconds())
      this.hourtime = getHourTime(this.hours)
      if (this.hourtime === 'PM' && this.hours !== 12) {
        this.hours12 = this.hours - 12
      }
      if (this.hourtime === 'PM' && this.hours === 12) {
        this.hours12 = 12
      }
      if (this.hourtime === 'AM' && this.hours > 23) {
        this.hour12 = 12
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.newClock {
  display: block;
  width: 100%;
  margin: 0 auto;
}
.content {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: flex-start;
  align-content: stretch;
  .content-left {
    order: 0;
    flex: 1 0 auto;
    align-self: flex-start;
    font-size: 6vw;
    text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.4);
  }
  .content-right {
    order: 0;
    flex: 0 0 auto;
    align-self: auto;
    .right-time {
      display: inline-flex;
      flex-direction: column;
      flex-wrap: nowrap;
      justify-content: center;
      align-content: center;
      align-items: center;
      height: 100%;
      .right-top {
        order: 0;
        flex: 0 1 auto;
        align-self: auto;
        font-size: 2.2vw;
        text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.4);
        &.noSeconds {
          height: 30%;
        }
      }
      .right-bottom {
        position: relative;
        top: -3px;
        order: 0;
        flex: 0 1 auto;
        align-self: auto;
        font-size: 2vw;
        text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.4);
        &.noAmPm {
          height: 30%;
        }
      }
    }
  }
}
.date-container {
  position: relative;
  top: -20px;
  .dateDay {
    font-size: 1.6vw;
    text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.4);
  }
}
</style>

<style lang="scss">
.clock-widget {
  .date-container {
    text-align: center;
  }
  .remove-btn {
    text-align: center;
    font-size: 12px;
    transition: 0s;
    opacity: 0;
    &.clock {
      position: relative;
      top: -20px;
    }
  }
  &:hover {
    cursor: pointer;
    .remove-btn {
      opacity: 0.8;
      transition-delay: 1.5s;
      text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.4);
    }
  }
}
.widget-top {
  &.widget-left {
    .clock-widget {
      justify-content: flex-start;
      padding-left: 20px;
    }
  }
  &.widget-center {
    .clock-widget {
      justify-content: center;
    }
  }
  &.widget-right {
    .clock-widget {
      justify-content: flex-end;
      padding-right: 20px;
    }
  }
}
.widget-middle {
  &.widget-left {
    .clock-widget {
      justify-content: flex-start;
      padding-left: 20px;
    }
  }
  &.widget-center {
    .clock-widget {
      justify-content: center;
    }
  }
  &.widget-right {
    .clock-widget {
      justify-content: flex-end;
      padding-right: 20px;
    }
  }
}
.widget-bottom {
  &.widget-left {
    .clock-widget {
      justify-content: flex-start;
      padding-left: 20px;
    }
  }
  &.widget-center {
    .clock-widget {
      justify-content: center;
    }
  }
  &.widget-right {
    .clock-widget {
      justify-content: flex-end;
      padding-right: 20px;
    }
  }
}
</style>
